import {
  InvoiceActionMenu,
  InvoiceActionMenuProps,
} from '@/components/RelationActionMenu';
import { Invoice } from '@/components/ReportForm/ApReportFormInputInvoiceFileField/type';
import {
  useOcrContext,
  AiProgressType as OcrAiProgressType,
} from '@/features/InvoiceReport/Edit/components/Context/OcrContext';
import { useOcrFileHandler } from '@/features/InvoiceReport/Edit/components/hooks/useOcrFileHandler';
import { useTranslation } from '@/i18n';
import { Checkbox } from '@moneyforward/ap-frontend-components';
import { useCallback, useMemo } from 'react';
import {
  ControllerRenderProps,
  FieldValues,
  Path,
  useFormContext,
} from 'react-hook-form';
import { AIProgressType } from './Components';
import styles from './InvoiceActionMenuWithOcr.module.scss';

type WrapInvoiceActionMenuWithOcrProps<TFieldValues extends FieldValues> = {
  fields: ControllerRenderProps<TFieldValues, Path<TFieldValues>>;
  inputId: string;
  invoice: Invoice;
} & Omit<InvoiceActionMenuProps, 'container'>;

export const InvoiceActionMenuWithOcr = <TFieldValues extends FieldValues>({
  fields,
  inputId,
  ...rest
}: WrapInvoiceActionMenuWithOcrProps<TFieldValues>) => {
  const { t } = useTranslation();
  const { handleFileChange: handleOcrFileChange } = useOcrFileHandler();

  const handleInvoiceSelected = useCallback(
    async (selectedInvoice: Invoice) => {
      await handleOcrFileChange(selectedInvoice);
    },
    [handleOcrFileChange]
  );

  const {
    isCheckedUseAiOcr,
    onChangeUseAiOcr,
    isPaymentRequestAiSuggestionFlag,
    onChangeAiProgressType,
  } = useOcrContext();
  const { control } = useFormContext<TFieldValues>();

  // Generate field name for AI progress type based on the current field name
  const aiProgressTypeFieldName = useMemo<Path<TFieldValues>>(() => {
    const baseName = String(fields.name);
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return `${baseName}_aiProgressType` as Path<TFieldValues>;
  }, [fields.name]);

  return (
    <div className={styles.container}>
      <div className={styles['dropdown-wrapper']}>
        <InvoiceActionMenu
          container={null}
          onSelected={handleInvoiceSelected}
          {...rest}
        />
        <input hidden id={inputId} {...fields} />
      </div>

      {isPaymentRequestAiSuggestionFlag ? (
        <div
          className={`${styles['dropdown-wrapper']} ${styles['ai-progress-type-wrapper']}`}
        >
          <AIProgressType<TFieldValues>
            control={control}
            name={aiProgressTypeFieldName}
            inputId={`${inputId}_ai-progress-type`}
            onChange={(value) => {
              if (typeof value === 'string') {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                onChangeAiProgressType(value as OcrAiProgressType);
              }
            }}
          />
        </div>
      ) : (
        <div className={styles['checkbox-wrapper']}>
          <Checkbox
            checked={isCheckedUseAiOcr}
            onChange={onChangeUseAiOcr}
            label={t('ai_ocr_reflection_label')}
            tooltip={t('ai_ocr_reflection_tooltip')}
            testId='ocr-reflection-checkbox'
          />
        </div>
      )}
    </div>
  );
};

InvoiceActionMenuWithOcr.displayName = 'InvoiceActionMenuWithOcr';
